import type { SetupDesignSnapshot, SetupTypeDesign } from '../shared/desktop-api';
import type { WorkingReadout } from './working-progress';

/**
 * PAIGE: SETUP'S FIELD DESIGN, QUESTION BY QUESTION.
 *
 * Production, 2026-10-02 ("Freight test", eight document types): Understand sat at
 * "Choosing useful fields for Bill of Lading · 0 of 8 document types with fields chosen ·
 * Left: Estimating…" for minutes, the page never changed, while the reasoner answered more
 * than forty questions for the eight types side by side. The engine now reports every
 * question asked and answered, and the fields each answer found, as one snapshot of the
 * whole design (SetupDesignSnapshot). This turns that snapshot into what the screen says:
 * every type in flight, the questions answered, the fields as they arrive, a time left
 * measured from the answers so far, and plainly when nothing has answered for too long.
 * Nothing here is animated or invented: every number is the engine's.
 */

/** What one type's card says while its fields are chosen. */
export type SetupTypeProgress = {
  /** Questions are being asked about it now. */
  working: boolean;
  /** The short line under its name: "3 of ~10 answered", "Waiting its turn", "6 fields". */
  status: string;
  /** What it has found so far (line items first, then the fields), or what it is asking. */
  detail: string;
  /** The whole story, for the card's tooltip. */
  title: string;
  /** Final fields once the type is ready, for the card's chips. */
  ready: { fields: string[]; lineItems: boolean } | null;
};

export type SetupDesignView = {
  /** "Choosing useful fields for 8 document types at once". */
  headline: string;
  /** "12 of ~31 questions answered", with "· 3 of 8 types done" once a type has finished. */
  detail: string;
  /** Time per question and how many at once, or why nothing is moving. */
  live: string;
  /** True when nothing has answered for much longer than a question takes. */
  stalled: boolean;
  /** Time left for the field design, measured from the answers so far; null before the first. */
  remainingMilliseconds: number | null;
  /** The Left fact: a measured estimate, or what it waits for. Never "Estimating…" forever. */
  left: string;
  types: Record<string, SetupTypeProgress>;
};

/** No first answer after this long reads as stalled (a question takes about a minute on a busy pool). */
export const FIRST_ANSWER_PATIENCE_MS = 3 * 60_000;
/** After the first answer: stalled at three questions' time, never under two minutes. */
const STALL_FLOOR_MS = 2 * 60_000;

const done = (type: SetupTypeDesign) => type.state === 'designed' || type.state === 'ready';
const finished = (type: SetupTypeDesign) => done(type) || type.state === 'failed';
const plural = (count: number, one: string, many: string) =>
  `${count.toLocaleString()} ${count === 1 ? one : many}`;

/** m:ss, or h:mm:ss past an hour. */
export function designClock(milliseconds: number) {
  const seconds = Math.max(0, Math.round(milliseconds / 1000));
  const minutes = Math.floor(seconds / 60);
  if (minutes >= 60)
    return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
}

/** Mean time from asking a question to its answer, at the concurrency the design runs at. */
export function timePerQuestion(design: SetupDesignSnapshot) {
  return design.questionsAnswered > 0
    ? design.answeredMilliseconds / design.questionsAnswered
    : null;
}

/**
 * Time left for the field design. Each type asks its questions one after another and
 * the types run `width` at a time, so the rest takes the longest type's remaining chain or
 * the remaining questions spread over the width, whichever is longer, at the measured time
 * per question. `ageMilliseconds` is how long ago the snapshot was taken.
 */
export function designRemaining(design: SetupDesignSnapshot, ageMilliseconds = 0) {
  const perQuestion = timePerQuestion(design);
  if (perQuestion === null) return null;
  const left = design.types
    .filter((type) => !finished(type))
    .map((type) => Math.max(0, type.planned - type.answered));
  if (left.length === 0) return 0;
  const chain = Math.max(...left);
  const spread = Math.ceil(left.reduce((sum, value) => sum + value, 0) / Math.max(1, design.width));
  return Math.max(0, Math.max(chain, spread) * perQuestion - Math.max(0, ageMilliseconds));
}

function typeProgress(type: SetupTypeDesign): SetupTypeProgress {
  const lineItems = type.tableColumns >= 2;
  const found = [...(lineItems ? ['Line items'] : []), ...type.fields];
  const ready = type.state === 'ready' ? { fields: type.fields, lineItems } : null;
  const working = type.state === 'asking' && type.asking > 0;
  const status =
    type.state === 'ready'
      ? plural(type.fields.length, 'field', 'fields')
      : type.state === 'designed'
        ? 'Fields chosen'
        : type.state === 'failed'
          ? 'Fields not chosen'
          : type.state === 'waiting'
            ? 'Waiting its turn'
            : `${type.answered.toLocaleString()} of ~${type.planned.toLocaleString()} answered`;
  const detail = found.length
    ? found.join(' · ')
    : working
      ? (type.question ?? 'Asking about its fields')
      : '';
  const title = [
    type.name,
    working && type.question ? `Now: ${type.question}` : '',
    `${plural(type.answered, 'question', 'questions')} answered`,
    found.length ? `Found: ${found.join(', ')}` : '',
  ]
    .filter(Boolean)
    .join('\n');
  return { working, status, detail, title, ready };
}

function headline(design: SetupDesignSnapshot) {
  const working = design.types.filter((type) => type.state === 'asking' && type.asking > 0);
  if (working.length === 1) return `Choosing useful fields for ${working[0].name}`;
  if (working.length > 1)
    return `Choosing useful fields for ${working.length.toLocaleString()} document types at once`;
  return design.types.every(finished) ? 'Finishing the field lists' : 'Choosing useful fields';
}

/** Why nothing is moving, in plain words; null while answers keep coming. */
function stallLine(design: SetupDesignSnapshot, silentFor: number, perQuestion: number | null) {
  if (design.questionsAsking === 0) return null;
  const patience =
    perQuestion === null ? FIRST_ANSWER_PATIENCE_MS : Math.max(STALL_FLOOR_MS, 3 * perQuestion);
  if (silentFor < patience) return null;
  const reason =
    design.lastFailure === 'unreachable'
      ? ' The last question could not reach Cortexa.'
      : design.lastFailure === 'timed-out'
        ? ' The last question ran out of time.'
        : '';
  const waiting = plural(design.questionsAsking, 'question is', 'questions are');
  return perQuestion === null
    ? `No answer from Cortexa yet after ${designClock(silentFor)}. ${waiting} waiting.${reason}`
    : `No answer from Cortexa for ${designClock(silentFor)}, longer than usual. ${waiting} still waiting.${reason}`;
}

/**
 * Everything the screen says about the field design, from the latest snapshot and how
 * long ago it was taken (the screen's own clock keeps the silence and the estimate moving
 * between snapshots).
 */
export function describeSetupDesign(
  design: SetupDesignSnapshot,
  ageMilliseconds = 0,
): SetupDesignView {
  const age = Math.max(0, ageMilliseconds);
  const perQuestion = timePerQuestion(design);
  const silentFor = design.sinceLastAnswerMilliseconds + age;
  const stall = stallLine(design, silentFor, perQuestion);
  const typesDone = design.types.filter(done).length;
  const allDone = design.types.length > 0 && design.types.every(finished);
  const total = allDone ? `${design.questionsPlanned}` : `~${design.questionsPlanned}`;
  const detail = [
    `${design.questionsAnswered.toLocaleString()} of ${total} questions answered`,
    // The types run side by side and finish together near the end, so "0 of 5 types done" would sit
    // still for minutes and then jump (Dustin, 2026-10-02): the count shows only once a type is done.
    typesDone > 0 ? `${typesDone} of ${design.types.length} types done` : '',
  ]
    .filter(Boolean)
    .join(' · ');
  const remaining = designRemaining(design, age);
  const live =
    stall ??
    (perQuestion === null
      ? design.questionsAsking > 0
        ? `Cortexa is answering the first ${plural(design.questionsAsking, 'question', 'questions')} · ${designClock(silentFor)}`
        : 'Getting the first questions ready'
      : `About ${designClock(perQuestion)} a question, ${plural(Math.max(1, design.questionsAsking), 'question', 'questions')} at once`);
  const left =
    remaining === null
      ? 'After the first answer'
      : stall
        ? 'Waiting on Cortexa'
        : remaining < 30_000
          ? 'Under a minute'
          : `~${designClock(remaining)} for fields`;
  return {
    headline: headline(design),
    detail,
    live,
    stalled: stall !== null,
    remainingMilliseconds: stall ? null : remaining,
    left,
    types: Object.fromEntries(design.types.map((type) => [type.name, typeProgress(type)])),
  };
}

/** The tags the type's card keeps once it is ready, as the engine's ready event carries them. */
export function readyTags(type: SetupTypeDesign) {
  return [...type.fields, ...(type.tableColumns >= 2 ? ['Line items'] : [])];
}

/**
 * The working screen's readout with the field design's own words in it: the headline names
 * every type being asked about, the detail counts questions answered, the live line gives the
 * time per question (or why nothing moves), and Left is the measured estimate. The bar, the
 * stepper and every other fact stay the readout's own.
 */
export function withSetupDesign(
  readout: WorkingReadout,
  view: SetupDesignView | null,
): WorkingReadout {
  if (!view || readout.stageId !== 'understanding') return readout;
  return {
    ...readout,
    headline: view.headline,
    detail: view.detail,
    valueText: `${view.headline}. ${view.detail}. ${view.live}`,
    live: { text: view.live },
    metrics: readout.metrics.map((metric) =>
      metric.label === 'Left' ? { label: 'Left', value: view.left } : metric,
    ),
    remainingMilliseconds: view.remainingMilliseconds,
  };
}
